import type { DevelopmentalNorm } from "../shared/types/domain";

// Rich landmark norms: 15 developmental breakpoints with real headsUp text,
// milestones, growth ranges, and WHO-style percentile data (boys/girls).
export const richNorms: DevelopmentalNorm[] = [
  {
    ageWeeks: 0,
    feedingMinMl: 420, feedingTargetMl: 550, diaperMin: 6, sleepNapsPerDay: 5,
    milestones: ["Reflexive movements (rooting, grasping)", "Turning head toward light", "Briefly quietens to familiar voices"],
    headsUp: "The 'Golden Month.' Baby is adjusting to life outside; focus on skin-to-skin contact and regaining birth weight (usually by day 10–14).",
    eventName: "Golden Month",
    growthMinGPerWeek: 150, growthMaxGPerWeek: 210,
    boys:  { p10: 2.9, p20: 3.0, p30: 3.2, p40: 3.3, p50: 3.5, p95: 4.4 },
    girls: { p10: 2.8, p20: 2.9, p30: 3.0, p40: 3.2, p50: 3.3, p95: 4.2 }
  },
  {
    ageWeeks: 4,
    feedingMinMl: 420, feedingTargetMl: 550, diaperMin: 6, sleepNapsPerDay: 5,
    milestones: ["Starting to track objects with eyes", "Lifting head briefly during tummy time", "Making small throaty sounds"],
    headsUp: "The 'Evening Fussiness' period often begins as baby processes new sensory input. A dark, quiet room can help them decompress.",
    eventName: "Evening Fussiness",
    growthMinGPerWeek: 150, growthMaxGPerWeek: 200,
    boys:  { p10: 3.9, p20: 4.1, p30: 4.3, p40: 4.4, p50: 4.5, p95: 5.5 },
    girls: { p10: 3.6, p20: 3.8, p30: 4.0, p40: 4.1, p50: 4.2, p95: 5.2 }
  },
  {
    ageWeeks: 6,
    feedingMinMl: 420, feedingTargetMl: 550, diaperMin: 6, sleepNapsPerDay: 4,
    milestones: ["The first social smile may emerge", "Extended cooing sounds", "Stays alert for longer stretches"],
    headsUp: "This is the typical 'Colic Peak.' It's a developmental peak in the nervous system — not a reflection of your parenting!",
    eventName: "Colic Peak",
    growthMinGPerWeek: 150, growthMaxGPerWeek: 200,
    boys:  { p10: 4.4, p20: 4.6, p30: 4.8, p40: 5.0, p50: 5.1, p95: 6.2 },
    girls: { p10: 4.0, p20: 4.2, p30: 4.4, p40: 4.5, p50: 4.6, p95: 5.7 }
  },
  {
    ageWeeks: 8,
    feedingMinMl: 420, feedingTargetMl: 550, diaperMin: 6, sleepNapsPerDay: 4,
    milestones: ["First social smiles", "Holding head up at 45 degrees", "Opening and shutting hands"],
    headsUp: "The 2-month growth spurt is common now. Baby may be extra hungry; follow their lead as their appetite increases.",
    eventName: "2-Month Growth Spurt",
    growthMinGPerWeek: 140, growthMaxGPerWeek: 180,
    boys:  { p10: 4.9, p20: 5.1, p30: 5.3, p40: 5.4, p50: 5.6, p95: 6.9 },
    girls: { p10: 4.5, p20: 4.7, p30: 4.9, p40: 5.0, p50: 5.1, p95: 6.3 }
  },
  {
    ageWeeks: 12,
    feedingMinMl: 560, feedingTargetMl: 700, diaperMin: 5, sleepNapsPerDay: 4,
    milestones: ["Pushing up on arms in tummy time", "Squealing with delight", "Watching their own hands move"],
    headsUp: "The 'Wakeful Window' — digestion has usually settled, and baby is becoming a social butterfly. Enjoy the newfound 'chatting'!",
    eventName: "Wakeful Window",
    growthMinGPerWeek: 130, growthMaxGPerWeek: 180,
    boys:  { p10: 5.6, p20: 5.9, p30: 6.1, p40: 6.3, p50: 6.4, p95: 7.9 },
    girls: { p10: 5.1, p20: 5.4, p30: 5.6, p40: 5.7, p50: 5.8, p95: 7.3 }
  },
  {
    ageWeeks: 16,
    feedingMinMl: 600, feedingTargetMl: 750, diaperMin: 5, sleepNapsPerDay: 3,
    milestones: ["Rolling from tummy to back", "Reaching and grabbing for toys", "Laughing out loud"],
    headsUp: "The 4-month sleep regression is common as sleep cycles mature. Consistency with your bedtime routine is your best tool.",
    eventName: "4-Month Sleep Regression",
    growthMinGPerWeek: 110, growthMaxGPerWeek: 160,
    boys:  { p10: 6.2, p20: 6.5, p30: 6.7, p40: 6.9, p50: 7.0, p95: 8.7 },
    girls: { p10: 5.6, p20: 5.9, p30: 6.1, p40: 6.3, p50: 6.4, p95: 8.1 }
  },
  {
    ageWeeks: 20,
    feedingMinMl: 630, feedingTargetMl: 790, diaperMin: 5, sleepNapsPerDay: 3,
    milestones: ["Starting to make 'razz' sounds (bubbles)", "Showing interest in your food", "Squealing with delight"],
    headsUp: "The 'drool fest' begins! Baby is discovering their voice and prepping for teething or early solids.",
    eventName: "Drool Fest & Teething Prep",
    growthMinGPerWeek: 100, growthMaxGPerWeek: 150,
    boys:  { p10: 6.7, p20: 7.0, p30: 7.2, p40: 7.4, p50: 7.5, p95: 9.4 },
    girls: { p10: 6.0, p20: 6.4, p30: 6.6, p40: 6.8, p50: 6.9, p95: 8.8 }
  },
  {
    ageWeeks: 24,
    feedingMinMl: 650, feedingTargetMl: 820, diaperMin: 5, sleepNapsPerDay: 3,
    milestones: ["Sitting with some support", "Responding to their own name", "Rolling in both directions"],
    headsUp: "Discovery window: Baby is learning cause and effect. Dropping a toy to watch you pick it up is a major cognitive lesson!",
    eventName: "Discovery Window",
    growthMinGPerWeek: 90, growthMaxGPerWeek: 130,
    boys:  { p10: 7.1, p20: 7.5, p30: 7.7, p40: 7.9, p50: 8.0, p95: 10.1 },
    girls: { p10: 6.4, p20: 6.8, p30: 7.1, p40: 7.2, p50: 7.3, p95: 9.5 }
  },
  {
    ageWeeks: 32,
    feedingMinMl: 650, feedingTargetMl: 820, diaperMin: 5, sleepNapsPerDay: 3,
    milestones: ["Starting to 'scoot' or crawl", "Passing toys from one hand to the other", "Showing 'Object Permanence'"],
    headsUp: "Separation anxiety can start as they realize you are a separate person. A consistent 'goodbye' ritual builds confidence.",
    eventName: "Separation Anxiety",
    growthMinGPerWeek: 80, growthMaxGPerWeek: 120,
    boys:  { p10: 7.8, p20: 8.2, p30: 8.5, p40: 8.7, p50: 8.9, p95: 11.2 },
    girls: { p10: 7.1, p20: 7.5, p30: 7.8, p40: 8.0, p50: 8.2, p95: 10.6 }
  },
  {
    ageWeeks: 36,
    feedingMinMl: 650, feedingTargetMl: 820, diaperMin: 5, sleepNapsPerDay: 2,
    milestones: ["Crawling or scooting", "Pulling to stand", "Understanding 'No'"],
    headsUp: "Mobility is hard work! You might see a brief plateau in weight as baby burns calories learning to navigate their world.",
    eventName: "Mobility Plateau",
    growthMinGPerWeek: 80, growthMaxGPerWeek: 120,
    boys:  { p10: 8.2, p20: 8.5, p30: 8.8, p40: 9.0, p50: 9.2, p95: 11.5 },
    girls: { p10: 7.5, p20: 7.9, p30: 8.2, p40: 8.4, p50: 8.5, p95: 10.9 }
  },
  {
    ageWeeks: 40,
    feedingMinMl: 650, feedingTargetMl: 820, diaperMin: 5, sleepNapsPerDay: 2,
    milestones: ["Pulling up to stand", "Using a pincer grasp (thumb and forefinger)", "Understanding 'No'"],
    headsUp: "Baby is likely 'cruising' along furniture. Double-check that your baby-proofing is sturdy as they gain height.",
    eventName: "Cruising Stage",
    growthMinGPerWeek: 70, growthMaxGPerWeek: 110,
    boys:  { p10: 8.4, p20: 8.8, p30: 9.1, p40: 9.3, p50: 9.5, p95: 11.9 },
    girls: { p10: 7.8, p20: 8.2, p30: 8.5, p40: 8.7, p50: 8.8, p95: 11.3 }
  },
  {
    ageWeeks: 52,
    feedingMinMl: 650, feedingTargetMl: 820, diaperMin: 5, sleepNapsPerDay: 2,
    milestones: ["Taking independent steps", "Saying 1–2 words", "Waving 'bye-bye'"],
    headsUp: "Happy first birthday! Growth velocity slows significantly now. Trust their cues; they are excellent at self-regulating calories.",
    eventName: "First Birthday",
    growthMinGPerWeek: 50, growthMaxGPerWeek: 100,
    boys:  { p10: 8.9, p20: 9.3, p30: 9.6, p40: 9.9, p50: 10.1, p95: 12.8 },
    girls: { p10: 8.2, p20: 8.7, p30: 9.0, p40: 9.2, p50: 9.4, p95: 12.2 }
  },
  {
    ageWeeks: 64,
    feedingMinMl: 350, feedingTargetMl: 500, diaperMin: 4, sleepNapsPerDay: 1,
    milestones: ["Walking well and starting to run", "Using a spoon (with some mess!)", "Pointing to show interest"],
    headsUp: "The 'Molar' window. Big back teeth can cause more discomfort than front ones. Chilled, soft foods are your best friend.",
    eventName: "Molar Window",
    growthMinGPerWeek: 40, growthMaxGPerWeek: 70,
    boys:  { p10: 9.6, p20: 10.1, p30: 10.4, p40: 10.7, p50: 10.9, p95: 13.7 },
    girls: { p10: 9.0, p20: 9.5, p30: 9.8, p40: 10.0, p50: 10.2, p95: 13.1 }
  },
  {
    ageWeeks: 78,
    feedingMinMl: 350, feedingTargetMl: 500, diaperMin: 4, sleepNapsPerDay: 1,
    milestones: ["Pointing to body parts", "Engaging in pretend play", "Using words and gestures to explain wants"],
    headsUp: "The 'Independence Peak.' They want to do things 'all by myself.' Offer two safe choices to help reduce power struggles.",
    eventName: "Independence Peak",
    growthMinGPerWeek: 35, growthMaxGPerWeek: 60,
    boys:  { p10: 10.4, p20: 10.9, p30: 11.2, p40: 11.4, p50: 11.6, p95: 14.7 },
    girls: { p10: 9.8, p20: 10.3, p30: 10.6, p40: 10.9, p50: 11.0, p95: 14.1 }
  },
  {
    ageWeeks: 92,
    feedingMinMl: 350, feedingTargetMl: 500, diaperMin: 4, sleepNapsPerDay: 1,
    milestones: ["Walking up stairs with help", "Kicking a ball", "Following two-step directions"],
    headsUp: "The language explosion is likely in full swing. Narrate your day like a sportscaster to help them soak up new vocabulary.",
    eventName: "Language Explosion",
    growthMinGPerWeek: 30, growthMaxGPerWeek: 55,
    boys:  { p10: 11.0, p20: 11.5, p30: 11.8, p40: 12.1, p50: 12.3, p95: 15.6 },
    girls: { p10: 10.4, p20: 10.9, p30: 11.3, p40: 11.6, p50: 11.7, p95: 15.1 }
  },
  {
    ageWeeks: 104,
    feedingMinMl: 350, feedingTargetMl: 500, diaperMin: 4, sleepNapsPerDay: 1,
    milestones: ["Using 2-word phrases", "Sorting shapes and colors", "Running and jumping with both feet"],
    headsUp: "Two years of growth! Every child's path is unique; if they aren't hitting a specific window yet, focus on the steady progress they are making.",
    eventName: "Second Birthday",
    growthMinGPerWeek: 30, growthMaxGPerWeek: 50,
    boys:  { p10: 11.5, p20: 12.0, p30: 12.4, p40: 12.7, p50: 12.9, p95: 16.4 },
    girls: { p10: 11.0, p20: 11.5, p30: 11.9, p40: 12.2, p50: 12.4, p95: 16.0 }
  }
];

// Returns the richNorm entry for the largest ageWeeks value <= week.
export function getNormForWeek(week: number): DevelopmentalNorm {
  let best = richNorms[0];
  for (const n of richNorms) {
    if (n.ageWeeks <= week) best = n;
    else break;
  }
  return best;
}
